import React from 'react';
import { DeepPageWithShell } from './DeepPage';
import { usePageMeta } from '../../lib/useMeta';
import { serviceSchema } from '../../lib/schema';

const PATH = '/kempo-karate';

const DATA = {
  breadcrumbs: [
    { label: 'Home', href: '/' },
    { label: 'The Art' },
    { label: 'Kempo Karate' },
  ],
  eyebrow: 'The Art',
  h1: 'What is Kempo Karate?',
  tagline: 'Fist Law \u00B7 Mind \u00B7 Body \u00B7 Spirit',
  intro:
    'Kempo \u2014 literally \u201CFist Law\u201D or \u201CWay of the Fist\u201D \u2014 is a traditional martial art that blends striking, grappling, and real-world self-defense. It carries an ancient lineage through China, Okinawa, Japan, and ultimately the American Kempo movement that shaped Universal Kempo Karate.',
  heroImage: 'https://customer-assets.emergentagent.com/job_kempo-patch/artifacts/pfzgm9ik_512675824_10235902812338064_8932337036377279083_n.jpeg',
  heroImageAlt: 'Kempo karate demonstration \u2014 Universal Kempo Karate',
  callouts: [
    { title: 'Complete System', body: 'Striking, blocks, grappling, locks, flow drills, and self-defense.' },
    { title: 'Adaptable', body: 'Kempo rewards timing and adaptability over brute force or rigid style.' },
    { title: 'Lineage You Can Train With', body: 'Our school has carried this art in Colorado Springs since 1980.' },
  ],
  sections: [
    {
      eyebrow: 'Origins',
      heading: 'From ancient China to Colorado Springs.',
      body: [
        'The roots of Kempo trace back to ancient Chinese martial arts that traveled through Okinawa and Japan over centuries. The word itself means \u201CFist Law\u201D \u2014 a reflection of its emphasis on both principle and application.',
        'In the mid-20th century, American Kempo Karate emerged with a strong emphasis on real-world self-defense and the integration of Mind, Body, and Spirit \u2014 the three pillars that appear on our school patch.',
        'Universal Kempo Karate has carried this lineage forward in Colorado Springs since 1980 \u2014 veteran-founded, family-run, and still teaching the art the way it was handed down.',
      ],
    },
    {
      eyebrow: 'What Makes Kempo Different',
      heading: 'A fighter\u2019s art tempered by a scholar\u2019s discipline.',
      body: [
        'Unlike sport-only karate, Kempo trains for the full range of real-world situations: close-quarters strikes, defensive flow, grappling and locks, and controlled sparring. The emphasis is on adaptability \u2014 using the right tool at the right distance.',
      ],
      bullets: [
        'Striking with hands, elbows, knees, and kicks',
        'Blocking and defensive flow drills',
        'Grappling fundamentals, joint locks, and sweeps',
        'Kata and traditional form study',
        'Scenario-based self-defense application',
      ],
    },
    {
      eyebrow: 'Mind. Body. Spirit.',
      heading: 'The three pillars of our training.',
      body: [
        'The patch worn by every Universal Kempo student represents the three pillars of our art: a focused Mind, a conditioned Body, and a disciplined Spirit. Training without all three is incomplete \u2014 and that is the standard we hold students to, from Little Dragons to Black Belt and beyond.',
      ],
    },
  ],
  ctaCopy: 'Experience Kempo \u2014 Free Trial',
  source: 'page-kempo-karate',
  bottomNote:
    'This page summarizes the Kempo tradition broadly. Specific lineage, kata, and curriculum vary by school. Our instructors will walk you through our exact lineage during your trial.',
};

const KempoKaratePage = () => {
  usePageMeta({
    title: 'Kempo Karate in Colorado Springs | Universal Kempo Karate',
    description:
      'Learn about Kempo karate (Fist Law) \u2014 origin, principles, and training. Universal Kempo Karate has taught the art in Colorado Springs since 1980.',
    path: PATH,
    breadcrumbs: DATA.breadcrumbs,
    schemas: [
      {
        id: 'kempo-service-jsonld',
        data: serviceSchema({
          name: 'Kempo Karate',
          description: 'Traditional Kempo karate instruction in Colorado Springs since 1980.',
          slug: PATH,
          serviceType: 'Martial Arts Instruction',
        }),
      },
    ],
  });
  return <DeepPageWithShell data={DATA} />;
};

export default KempoKaratePage;
